/**
 * Fetches addresses for a given postcode using the Ideal Postcodes API and postcodes.io.
 * Throws an error with the relevant message if the postcode is invalid, terminated, or no addresses are found.
 * @param postcode
 * @param idealPostcodesApiKey
 * @return {Promise<{error: string}|{formattedPostcode: any, addresses: *}>}
 */
export async function fetchPostcodeAddresses(postcode, idealPostcodesApiKey) {
  const trimmedPostcode = postcode.replace(/\s+/g, '');

  /* postcodes.io lookup */
  const formattedPostcode = await fetch(`https://api.postcodes.io/postcodes/${trimmedPostcode}`)
    .then(response => response.json()).then(data => {
      if (data.status !== 200) {
        return null;
      }
      return data.result.postcode;
    })
    .catch(error => null);

  if (!formattedPostcode) {
    return {
      error: 'Postcode not found or invalid.'
    }
  }

  /* postcodes.io check uf terminated */
  const isTerminated = await fetch(`https://api.postcodes.io/terminated_postcodes/${trimmedPostcode}`)
    .then(() => false)
    .catch(() => true)

  if (isTerminated) {
    return {
      error: 'This postcode is terminated and cannot be used.'
    }
  }

  /* idealpostcodes fetch list of addresses */
  const addresses = await fetch(`https://api.ideal-postcodes.co.uk/v1/postcodes/${trimmedPostcode}?api_key=${idealPostcodesApiKey}&filter=line_1,line_2,line_3,post_town,postcode,udprn,longitude,latitude`)
    .then(response => response.json())
    .then(data => {
      if (data.code === 2000) {
        return data.result;
      }
      return null;
    })
    .catch(() => null);

  if (!addresses || addresses.length === 0) {
    return {
      error: 'No addresses found for this postcode.'
    }
  }

  return {
    formattedPostcode, addresses
  };
}

/**
 * Fetches the Place ID for a given address using Google Places API. Used when selecting a postcode result.
 * @param addressString
 * @param bounds
 * @return {Promise<null|string>}
 */
export async function fetchPlaceIdForPostCodeResult(addressString, bounds) {
  const {Place} = await google.maps.importLibrary('places');
  const response = await Place.searchByText({
    textQuery: addressString,
    maxResultCount: 1,
    fields: ['id'],
    locationRestriction: bounds
  });

  if (response.places.length > 0) {
    return response.places[0].id;
  }
  return null;
}
